import React from 'react';
import { Box } from '@mui/material';
import SurfaceCard from '../../common/SurfaceCard';
import ShimmerBox from './ShimmerBox';
import TableShimmer from './TableShimmer';

const FieldShimmer: React.FC<{ labelWidth?: string; inputClassName?: string }> = ({
  labelWidth = 'w-16',
  inputClassName = 'h-[40px]',
}) => (
  <div className="min-w-0 space-y-2">
    <ShimmerBox className={`h-3 rounded ${labelWidth}`} />
    <ShimmerBox className={`w-full rounded-lg ${inputClassName}`} />
  </div>
);

const ProhanceToolbarShimmer: React.FC = () => (
  <SurfaceCard
    className="mb-4 md:min-h-[72px]"
    contentClassName="p-4 md:p-[18px_22px] md:pl-[26px]"
  >
    <div className="relative z-[1] flex flex-col items-stretch gap-3 md:flex-row md:items-end md:justify-end">
      <div className="w-full md:max-w-[280px]">
        <ShimmerBox className="h-[34px] w-full rounded-lg" />
      </div>
      <ShimmerBox className="h-9 w-[112px] rounded-[10px]" />
      <ShimmerBox className="h-9 w-[88px] rounded-[10px]" />
      <ShimmerBox className="h-9 w-[88px] rounded-[10px]" />
    </div>
  </SurfaceCard>
);

const FalconToolbarShimmer: React.FC = () => (
  <SurfaceCard
    className="mb-4 md:min-h-[92px]"
    contentClassName="p-4 md:p-[18px_22px] md:pl-[26px]"
  >
    <div className="relative z-[1] grid grid-cols-1 items-end gap-4 md:grid-cols-[150px_150px_auto_minmax(200px,280px)] md:gap-6">
      <FieldShimmer labelWidth="w-24" inputClassName="h-[40px]" />
      <FieldShimmer labelWidth="w-12" inputClassName="h-[40px]" />
      <ShimmerBox className="h-9 w-[108px] rounded-[10px]" />
      <ShimmerBox className="h-[34px] w-full rounded-lg" />
    </div>
  </SurfaceCard>
);

const ManageJobsFilterShimmer: React.FC = () => (
  <SurfaceCard
    className="mb-4 md:min-h-[92px]"
    contentClassName="p-4 md:p-[18px_22px] md:pl-[26px]"
  >
    <div className="relative z-[1] grid grid-cols-1 items-end gap-4 md:grid-cols-4 md:gap-6">
      {[0, 1, 2, 3].map((item) => (
        <FieldShimmer key={item} labelWidth="w-20" inputClassName="h-[40px]" />
      ))}
    </div>
  </SurfaceCard>
);

export type AutoProvisioningShimmerVariant = 'prohance' | 'falcon' | 'manageJobs';

export interface AutoProvisioningPageShimmerProps {
  variant: AutoProvisioningShimmerVariant;
  tableColumns?: number;
  tableRows?: number;
  tableMinHeight?: number | string;
}

const AutoProvisioningPageShimmer: React.FC<AutoProvisioningPageShimmerProps> = ({
  variant,
  tableColumns = 6,
  tableRows = 10,
  tableMinHeight = 'calc(100vh - 280px)',
}) => (
  <Box
    data-testid={`${variant}-page-shimmer`}
    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }}
  >
    {variant === 'prohance' && <ProhanceToolbarShimmer />}
    {variant === 'falcon' && <FalconToolbarShimmer />}
    {variant === 'manageJobs' && <ManageJobsFilterShimmer />}

    <Box sx={{ flex: 1, minHeight: 0 }}>
      <TableShimmer
        columns={tableColumns}
        rows={tableRows}
        minHeight={tableMinHeight}
        className="shadow-none"
      />
    </Box>
  </Box>
);

export default AutoProvisioningPageShimmer;
